import dayjs, { type Dayjs } from "dayjs";
import type { Kpi as SchemaKpi, Timeframe, V1CustomerRef } from "@acto/ambient-context-schema";
import type Customer from "@acto/taskpane/core/domain/Customer";
import { Kpi } from "@acto/taskpane/core/domain/KpiAnalysis";

/** Format a Date / Dayjs / date-string to the schema's YYYY-MM-DD (no tz drift). */
export const toIsoDate = (d: Date | Dayjs | string | undefined): string | undefined =>
  d ? dayjs(d).format("YYYY-MM-DD") : undefined;

/** A client → the minimal { external_reference, name } reference used in v1 list scopes. */
export const toCustomerRef = (c: Customer): V1CustomerRef => ({
  external_reference: c.externalReference,
  name: c.name,
});

/**
 * Bound a scope list. Every list-producing mapper routes through this so a
 * per-list cap is a one-line change at the call site; `limit` undefined sends
 * everything visible (the current default).
 */
export const capList = <T>(items: readonly T[], limit?: number): T[] =>
  limit === undefined ? [...items] : items.slice(0, limit);

/** Structural mirror of the fs2 PeriodSelection, so core mappers don't import fs2 modules. */
export type PeriodSelectionInput =
  | { kind: "preset"; preset: string }
  | { kind: "custom"; range: { startDate: string; endDate: string } };

const PRESET_TO_TIMEFRAME: Record<string, Timeframe> = {
  LAST_MONTH: "1m",
  LAST_3_MONTHS: "3m",
  LAST_6_MONTHS: "6m",
  LAST_12_MONTHS: "12m",
  LAST_24_MONTHS: "24m",
  YEAR_TO_DATE: "ytd",
};

export interface TimeframeFields {
  timeframe?: Timeframe;
  timeframe_start?: string;
  timeframe_end?: string;
}

/** The app's period selection → the schema's timeframe token (+ start/end for custom ranges). */
export function toTimeframe(selection: PeriodSelectionInput | undefined): TimeframeFields {
  if (!selection) return {};
  if (selection.kind === "custom") {
    return {
      timeframe: "custom",
      timeframe_start: toIsoDate(selection.range.startDate),
      timeframe_end: toIsoDate(selection.range.endDate),
    };
  }
  const timeframe = PRESET_TO_TIMEFRAME[selection.preset];
  return timeframe ? { timeframe } : {};
}

const KPI_TO_SCHEMA: Record<Kpi, SchemaKpi> = {
  [Kpi.REVENUE]: "revenue",
  [Kpi.MARGIN]: "margin",
  [Kpi.ORDERS]: "orders",
  [Kpi.PROFIT]: "profit",
};

/** The app's Kpi enum → the schema's lowercase token. */
export const toSchemaKpi = (kpi: Kpi): SchemaKpi => KPI_TO_SCHEMA[kpi];
